import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';

import ScheduleDeliveryHistoryModal from './ScheduleDeliveryHistoryModal';
import ScheduleReportModal from './ScheduleReportModal';

import { getDateFnsLocale } from 'Helpers/localeService';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import SectionTile from 'CommonComponents/SectionTile';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import SelectField from 'CommonComponents/SelectField';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import useApiRequest from 'Hooks/useApiRequest';
import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import usePagination from 'Hooks/usePagination';
import { useToast } from 'Hooks/useToast';

import ClockIcon from 'Assets/icons/clock.svg';
import DotsIcon from 'Assets/icons/dots-horizontal.svg';
import EditIcon from 'Assets/icons/edit.svg';
import PlayCircleIcon from 'Assets/icons/play-circle.svg';
import RefreshSyncIcon from 'Assets/icons/refresh-sync.svg';
import TrashIcon from 'Assets/icons/trash.svg';
import WarningTriangleIcon from 'Assets/icons/warning-triangle.svg';

const STATUS_OPTIONS = [
    { value: '', label: $t('All') },
    { value: 'active', label: $t('Active') },
    { value: 'paused', label: $t('Paused') },
    { value: 'failed', label: $t('Failed') },
];

const FREQUENCY_OPTIONS = [
    { value: '', label: $t('All') },
    { value: 'preset', label: $t('Preset') },
    { value: 'specific', label: $t('Specific dates') },
    { value: 'cron', label: $t('Custom (advanced)') },
];

const STATUS_BADGE = {
    active: { variant: 'success', label: $t('Active') },
    paused: { variant: 'neutral', label: $t('Paused') },
    failed: { variant: 'danger', label: $t('Failed') },
};

const formatDateTime = (value) => (value ? format(new Date(value), 'd MMM yyyy, HH:mm', { locale: getDateFnsLocale() }) : '-');

/**
 * "Scheduled Reports" tab: the list of recurring report deliveries. Mirrors the
 * Generated Reports tab (usePagination + useApiRequest + SectionTile + filters +
 * a per-row overflow menu). Standard users see only their own schedules;
 * report-centre admins get a My / All segmented switch. Row actions are Edit
 * (owner-only), Pause/Resume and Delete — Run now and delivery history arrive
 * with sub-tickets B and C.
 */
export default function ScheduledReportsList({
    indexUrl,
    updateUrl,
    statusUrl,
    destroyUrl,
    runUrl,
    runsUrl,
    previewUrl,
    isAdmin = false,
    defaultTimezone = 'UTC',
    reports = [],
}) {
    const pagination = usePagination({ perPage: 25 });
    const toast = useToast();
    const confirm = useConfirmationModal();
    const editModal = useModal();
    const historyModal = useModal();

    const [filters, setFilters] = useState({ report_key: '', frequency_type: '', status: '' });
    // Admins default to seeing every schedule in the event (matches the
    // documented manual-test expectation); the My/All toggle still lets them
    // switch to 'mine'. Non-admins have no toggle and are always 'mine'.
    const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');
    const [editing, setEditing] = useState(null);
    const [editNonce, setEditNonce] = useState(0);
    const [historySchedule, setHistorySchedule] = useState(null);
    const [historyNonce, setHistoryNonce] = useState(0);
    // Tracks which single row's "Run now" request is in flight — runRequest is one
    // shared hook instance, so gating on its own `loading` would disable every row
    // while any one row is running. Keyed by id so independent rows stay usable.
    const [runningRowId, setRunningRowId] = useState(null);

    const listRequest = useApiRequest({ url: indexUrl });
    const statusRequest = useApiRequest({ url: statusUrl });
    const deleteRequest = useApiRequest({ url: destroyUrl });
    const runRequest = useApiRequest({ url: runUrl });

    const reportsByKey = useMemo(() => Object.fromEntries((reports || []).map((report) => [report.type, report])), [reports]);
    const reportOptions = useMemo(
        () => [{ value: '', label: $t('All') }, ...(reports || []).map((report) => ({ value: report.type, label: report.name }))],
        [reports],
    );

    const queryParams = useMemo(
        () => ({
            page: pagination.page,
            per_page: pagination.perPage,
            ...(filters.report_key ? { report_key: filters.report_key } : {}),
            ...(filters.frequency_type ? { frequency_type: filters.frequency_type } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(isAdmin ? { scope } : {}),
        }),
        [pagination.page, pagination.perPage, filters, isAdmin, scope],
    );

    const getSchedules = useCallback(() => {
        listRequest.get(queryParams);
    }, [queryParams]);

    useEffect(() => {
        getSchedules();
    }, [getSchedules]);

    useEffect(() => {
        if (listRequest.data?.meta) {
            const { meta } = listRequest.data;
            pagination.setFrom(meta.from);
            pagination.setTo(meta.to);
            pagination.setTotal(meta.total);
            pagination.setLastPage(meta.last_page);
        }
    }, [listRequest.data]);

    const setFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        pagination.setPage(1);
    };

    const openEdit = (row) => {
        setEditing(row);
        setEditNonce((nonce) => nonce + 1);
        editModal.show();
    };

    const openHistory = (row) => {
        setHistorySchedule(row);
        setHistoryNonce((nonce) => nonce + 1);
        historyModal.show();
    };

    const runNow = (row) => {
        // Debounce per row: a run-now fires a real generation and emails every
        // recipient, so a double-click on the SAME row must never dispatch it
        // twice. Ignore re-entry while THIS row's run request is in flight (the
        // menu item is also disabled meanwhile) — a different row may still run.
        if (runningRowId === row.id) {
            return;
        }

        setRunningRowId(row.id);

        runRequest
            .request({ method: 'post', url: runUrl.replace('REPORT_SCHEDULE_ID', row.id) })
            .then(() => toast.success($t('Report is being generated and will be emailed to recipients shortly.')))
            .catch(() => toast.error($t('Failed to run the schedule. Please try again.')))
            .finally(() => setRunningRowId((current) => (current === row.id ? null : current)));
    };

    const toggleStatus = (row) => {
        const nextStatus = row.status === 'active' ? 'paused' : 'active';

        statusRequest
            .request({ method: 'patch', url: statusUrl.replace('REPORT_SCHEDULE_ID', row.id), data: { status: nextStatus } })
            .then(() => {
                toast.success(nextStatus === 'paused' ? $t('Schedule paused.') : $t('Schedule resumed.'));
                getSchedules();
            })
            .catch(() => toast.error($t('Failed to update the schedule. Please try again.')));
    };

    const deleteSchedule = (row) => {
        confirm({
            title: $t('Delete schedule?'),
            message: $t('This permanently removes the schedule and stops its deliveries. This cannot be undone.'),
            actions: [
                { confirmed: false, label: $t('Cancel'), variant: 'secondary', onClick: () => {} },
                {
                    confirmed: true,
                    label: $t('Delete permanently'),
                    variant: 'danger',
                    onClick: () => {
                        deleteRequest
                            .request({ method: 'delete', url: destroyUrl.replace('REPORT_SCHEDULE_ID', row.id) })
                            .then(() => {
                                const currentPageRowCount = listRequest.data?.data?.length ?? 0;
                                if (pagination.page > 1 && currentPageRowCount === 1) {
                                    pagination.setPage(pagination.page - 1);
                                } else {
                                    getSchedules();
                                }
                            })
                            .catch(() => toast.error($t('Failed to delete the schedule. Please try again.')));
                    },
                },
            ],
        });
    };

    const rows = listRequest.data?.data || [];
    const total = listRequest.data?.meta?.total ?? 0;
    const columnCount = 7;

    return (
        <div className="tw-space-y-4">
            <SectionTile header={$t('Scheduled Reports')} defaultCollapsed={false} visibly>
                <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-4">
                    <FilterField label={$t('Report')} className="tw-w-56">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold"
                            backgroundColor="#e4e4e480"
                            value={filters.report_key}
                            options={reportOptions}
                            onChange={(option) => setFilter('report_key', option ? option.value : '')}
                        />
                    </FilterField>

                    <FilterField label={$t('Frequency')} className="tw-w-44">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold"
                            backgroundColor="#e4e4e480"
                            value={filters.frequency_type}
                            options={FREQUENCY_OPTIONS}
                            onChange={(option) => setFilter('frequency_type', option ? option.value : '')}
                        />
                    </FilterField>

                    <FilterField label={$t('Status')} className="tw-w-44">
                        <SelectField
                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold"
                            backgroundColor="#e4e4e480"
                            value={filters.status}
                            options={STATUS_OPTIONS}
                            onChange={(option) => setFilter('status', option ? option.value : '')}
                        />
                    </FilterField>

                    {isAdmin && (
                        <FilterField label={null}>
                            <SegmentedControl
                                value={scope}
                                onChange={setScope}
                                options={[
                                    { value: 'mine', label: $t('My schedules') },
                                    { value: 'all', label: $t('All users') },
                                ]}
                            />
                        </FilterField>
                    )}

                    <Button variant="secondary" small onClick={getSchedules} className="tw-ms-auto tw-flex tw-items-center tw-gap-1.5">
                        <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                        {$t('Refresh')}
                    </Button>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            currentPage={pagination.page}
                        />
                    </div>
                    <PerPageSelect
                        value={pagination.perPage}
                        options={[10, 25, 50, 100]}
                        onChange={(event) => pagination.setPerPage(parseInt(event.target.value, 10))}
                    />
                </div>

                <div className="inbud-table_wrapper">
                    <table className="inbud-table">
                        <thead>
                            <tr>
                                <th>{$t('Name')}</th>
                                <th>{$t('Report')}</th>
                                <th>{$t('Frequency')}</th>
                                <th className="tw-w-40 tw-min-w-40">{$t('Next run')}</th>
                                <th className="tw-w-40 tw-min-w-40">{$t('Last run')}</th>
                                <th className="tw-w-32 tw-min-w-32">{$t('Status')}</th>
                                <th className="tw-w-24 tw-min-w-24 tw-text-end">{$t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listRequest.loading && <TableRowSkeleton rows={5} columns={columnCount} />}

                            {!listRequest.loading &&
                                rows.map((row) => {
                                    const badge = STATUS_BADGE[row.status] || STATUS_BADGE.active;

                                    return (
                                        <tr key={row.id}>
                                            <td>
                                                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                                    {row.name}
                                                    {row.has_bounced_recipients && (
                                                        <span
                                                            title={$t('One or more recipients have recently bounced.')}
                                                            aria-label={$t('One or more recipients have recently bounced.')}
                                                        >
                                                            <WarningTriangleIcon className="tw-w-3.5 tw-h-3.5 tw-text-red-600" />
                                                        </span>
                                                    )}
                                                </span>
                                            </td>
                                            <td>{row.report_label || row.report_key}</td>
                                            <td>{row.frequency_human}</td>
                                            <td className="inbud-date-cell">{formatDateTime(row.next_run_at)}</td>
                                            <td className="inbud-date-cell">{formatDateTime(row.last_run_at)}</td>
                                            <td>
                                                <Badge variant={badge.variant}>{badge.label}</Badge>
                                            </td>
                                            <td className="tw-text-center">
                                                <RowMenu
                                                    row={row}
                                                    canEditReport={Boolean(reportsByKey[row.report_key])}
                                                    isRunning={runningRowId === row.id}
                                                    onRunNow={() => runNow(row)}
                                                    onEdit={() => openEdit(row)}
                                                    onViewHistory={() => openHistory(row)}
                                                    onToggleStatus={() => toggleStatus(row)}
                                                    onDelete={() => deleteSchedule(row)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}

                            {!listRequest.loading && total === 0 && (
                                <tr>
                                    <td colSpan={columnCount}>
                                        <div className="tw-py-12 tw-text-center">
                                            <div className="tw-mx-auto tw-mb-3.5 tw-h-12 tw-w-12 tw-rounded-full tw-bg-charcoal-15" />
                                            <div className="tw-font-medium tw-text-charcoal">{$t('No scheduled reports yet')}</div>
                                            <div className="tw-mt-1.5 tw-text-sm tw-text-charcoal-75">
                                                {$t('Open a report from the Report tab and choose “Schedule this report” to set up recurring delivery.')}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SectionTile>

            <ScheduleReportModal
                modal={editModal}
                mode="edit"
                nonce={editNonce}
                report={editing ? reportsByKey[editing.report_key] : null}
                schedule={editing}
                defaultTimezone={defaultTimezone}
                updateUrl={updateUrl}
                previewUrl={previewUrl}
                onSaved={() => {
                    editModal.hide();
                    getSchedules();
                }}
            />

            <ScheduleDeliveryHistoryModal modal={historyModal} runsUrl={runsUrl} schedule={historySchedule} nonce={historyNonce} />
        </div>
    );
}

function FilterField({ label, className, children }) {
    return (
        <div className={className}>
            {label ? (
                <label className="text-input__label tw-block tw-mb-1">{label}</label>
            ) : (
                // Alignment spacer for filters with no visible label (e.g. the admin
                // scope toggle) — aria-hidden so screen readers don't announce it.
                <span className="text-input__label tw-block tw-mb-1" aria-hidden="true">
                    &nbsp;
                </span>
            )}
            {children}
        </div>
    );
}

function RowMenu({ row, canEditReport, isRunning = false, onRunNow, onEdit, onViewHistory, onToggleStatus, onDelete }) {
    const [open, setOpen] = useState(false);
    const paused = row.status === 'paused';

    const menu = (
        <div className="tw-flex tw-flex-col tw-min-w-40">
            {row.can_run && (
                <TextButton
                    disabled={isRunning}
                    onClick={() => {
                        if (isRunning) {
                            return;
                        }

                        setOpen(false);
                        onRunNow();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                >
                    <PlayCircleIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('Run now')}
                </TextButton>
            )}
            {row.can_edit && canEditReport && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        onEdit();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <EditIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('Edit')}
                </TextButton>
            )}
            {row.can_view_history && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        onViewHistory();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <ClockIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('View delivery history')}
                </TextButton>
            )}
            {row.can_pause && (
                <TextButton
                    onClick={() => {
                        setOpen(false);
                        onToggleStatus();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <RefreshSyncIcon className="tw-w-3.5 tw-h-3.5" />
                    {paused ? $t('Resume') : $t('Pause')}
                </TextButton>
            )}
            {row.can_delete && (
                <TextButton
                    variant="danger"
                    onClick={() => {
                        setOpen(false);
                        onDelete();
                    }}
                    className="tw-flex tw-w-full tw-justify-start tw-px-3 tw-py-2 tw-rounded-lg hover:tw-bg-charcoal-15"
                >
                    <TrashIcon className="tw-w-3 tw-h-3" />
                    {$t('Delete')}
                </TextButton>
            )}
        </div>
    );

    return (
        <Fragment>
            <Tooltip
                trigger="click"
                tooltipType="content"
                placement="bottom-end"
                interactive
                visible={open}
                onVisibleChange={setOpen}
                backgroundClass="tw-bg-white tw-border tw-border-grey !tw-opacity-100 !tw-p-1"
                tooltipContent={menu}
            >
                <button type="button" title={$t('Actions')} aria-label={$t('Actions')}>
                    <DotsIcon className="tw-w-3.5 tw-h-3.5" />
                </button>
            </Tooltip>
        </Fragment>
    );
}
